/**
 * DisplayTab - Display preferences (language and theme).
 * Per CONTEXT.md: Per-user settings stored in localStorage.
 */

import { useTranslation } from 'react-i18next';
import type { DisplayPreferences } from '../../types/company';
import { setThemeMode } from '../../utils/theme';

interface DisplayTabProps {
  preferences: DisplayPreferences;
  onChange: (prefs: DisplayPreferences) => void;
}

export default function DisplayTab({ preferences, onChange }: DisplayTabProps) {
  const { t, i18n } = useTranslation('companySettings');

  const handleLanguageChange = (language: 'en' | 'ar') => {
    onChange({ ...preferences, language });
    // Apply language change immediately
    i18n.changeLanguage(language);
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = language;
    localStorage.setItem('safaqat24.display.language', language);
  };

  const handleThemeChange = (theme: 'light' | 'dark' | 'system') => {
    onChange({ ...preferences, theme });
    // Persist + apply + broadcast through the shared theme module so the navbar
    // toggle and every other consumer stay in sync.
    setThemeMode(theme);
  };

  return (
    <div className="space-y-8">
      {/* Section header */}
      <div>
        <h2 className="text-lg font-semibold text-[var(--pv-color-primary)]">{t('display.heading')}</h2>
        <p className="mt-1 text-sm text-gray-500">{t('display.description')}</p>
      </div>

      {/* Language selection */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-base font-medium text-gray-900 mb-1">
          {t('display.language.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('display.language.description')}
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => handleLanguageChange('en')}
            className={`
              flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors
              ${preferences.language === 'en'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('display.language.english')}
          </button>
          <button
            onClick={() => handleLanguageChange('ar')}
            className={`
              flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors
              ${preferences.language === 'ar'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('display.language.arabic')}
          </button>
        </div>
      </div>

      {/* Theme selection */}
      <div className="bg-gray-50 rounded-lg p-6">
        <h3 className="text-base font-medium text-gray-900 mb-1">
          {t('display.theme.title')}
        </h3>
        <p className="text-sm text-gray-500 mb-4">
          {t('display.theme.description')}
        </p>
        <div className="flex gap-4">
          <button
            onClick={() => handleThemeChange('light')}
            className={`
              flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors
              ${preferences.theme === 'light'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('display.theme.light')}
          </button>
          <button
            onClick={() => handleThemeChange('dark')}
            className={`
              flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors
              ${preferences.theme === 'dark'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('display.theme.dark')}
          </button>
          <button
            onClick={() => handleThemeChange('system')}
            className={`
              flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium transition-colors
              ${preferences.theme === 'system'
                ? 'border-[#004D40] bg-[var(--pv-color-surface-sunken)] text-[#003930]'
                : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
              }
            `}
          >
            {t('display.theme.system')}
          </button>
        </div>
      </div>

      {/* Note about local storage */}
      <p className="text-sm text-gray-500 italic">
        {t('display.note')}
      </p>
    </div>
  );
}
